import React, { useState } from 'react';
import { useHistory } from 'react-router';
import { useSelector } from 'react-redux';
import Tabs, { TabPane } from 'rc-tabs';
import classNames from 'classnames';
import { accessibleOnClick } from '../../../../shared/utils/accessible-on-click';
import * as Icons from '../../../../shared/svg';
import PeopleTabComponent from '../people-tab';
import {
  FilterComponentType,
  LeadFinderTabKey,
  SavedTabFilters,
} from '../../type';
import SavedTab from '../saved-tab/saved-tab';
import { useSavedLeadFilter } from '../hooks/useLeadSavedFilter';
import LeadsFilterBulkSearch from '../leads-filter-bulk-search/leads-filter-bulk-search';
import { BulkFilters } from '../leads-filter-bulk-search/type';
import { useLeadFilter } from '../hooks/useLeadFilter';
import { getLeadListingPageSize } from '../../helpers/helper';
import { leadFinderViewOnly } from '../../../../shared/utils/user-details';
import { RootState } from '../../../../store/root-reducer';

interface LeadsSidebarProps {
  isSidebarExpanded: any;
  setIsSidebarExpanded: any;
  handleRecentSearch: any;
  handleGeneratePayload: any;
  activeTabKey: string;
  setActiveTabKey: (d: string) => void;
  handlePagination: (d?: any) => void;
  // eslint-disable-next-line react/require-default-props
  setLeadsData?: any;
  setSelectedLeads: (d?: any) => void;
  handleRouteChange: (d?: any) => void;
  leadTags: any[];
  isLeadTagsLoading: boolean;
  fetchLeadTags: (applyLoading?: boolean) => void;
  handleResetStateAfterAnyAction: () => void;
  savedFilters: SavedTabFilters;
  setSavedFilters: React.Dispatch<React.SetStateAction<SavedTabFilters>>;
  showPhoneNumberNudge: boolean;
  setShowPhoneNumberNudge: React.Dispatch<React.SetStateAction<boolean>>;
  setIgnorePhoneNumberNudge: React.Dispatch<React.SetStateAction<boolean>>;
  searchLeads: (payload?: any, performSearch?: boolean) => void;
  isLoading: boolean;
  keywordInputRef: React.RefObject<HTMLInputElement>;
}

const LeadsSidebar = (props: LeadsSidebarProps) => {
  const {
    isSidebarExpanded,
    setIsSidebarExpanded,
    handleRecentSearch,
    handleGeneratePayload,
    activeTabKey,
    setActiveTabKey,
    setLeadsData,
    setSelectedLeads,
    handleRouteChange,
    leadTags,
    isLeadTagsLoading,
    handleResetStateAfterAnyAction,
    savedFilters,
    setSavedFilters,
    showPhoneNumberNudge,
    setShowPhoneNumberNudge,
    setIgnorePhoneNumberNudge,
    searchLeads,
    isLoading,
    keywordInputRef,
  } = props;

  const history = useHistory();

  const leadFinder = useSelector((state: RootState) => state?.home?.leadFinder);
  const isRestrictedV1Access = leadFinder?.restrictedV1Access === true;

  const [showBulkSearch, setShowBulkSearch] = useState(false);
  const [
    bulkSearchFocusFilterKey,
    setBulkSearchFocusFilterKey,
  ] = useState<string>();
  const [bulkFilterSyncKey, setBulkFilterSyncKey] = useState<
    Record<string, boolean>
  >({});
  const { handleDefaultValues } = useSavedLeadFilter(activeTabKey);

  const {
    handleDeleteFilter,
    handleGetAllKeysByFilterFromQuery,
    handleIndividualTag,
    handleFoundValueExceptFromList,
    handleQuery,
    handleBulkQuery,
  } = useLeadFilter(activeTabKey, searchLeads);

  const onSidebarExpansionChange = () => {
    setIsSidebarExpanded(!isSidebarExpanded);
  };

  const handleTabChange = (key) => {
    if (key !== activeTabKey) {
      setLeadsData({
        profiles: [],
        pagination: null,
      });
      handleResetStateAfterAnyAction();
      setActiveTabKey(key);
      handleRouteChange();
      setSavedFilters(handleDefaultValues(true));
      const queryParams = new URLSearchParams();
      if (key === LeadFinderTabKey.SAVED_TAB) {
        queryParams.set('start', '1');
        queryParams.set('take', getLeadListingPageSize().toString());
      }
      if (key === LeadFinderTabKey.PEOPLE_TAB) {
        queryParams.delete('start');
        queryParams.delete('take');
      }
      history.push({ search: queryParams.toString(), hash: key });
    }
  };

  const onBulkSearchShow = (filterKey) => {
    setBulkSearchFocusFilterKey(filterKey);
    setShowBulkSearch(true);
  };
  const onBulkSearch = (bulkFilters: BulkFilters) => {
    setShowBulkSearch(false);

    const bulkQuery = [];
    const newBulkFilterSyncKeys: Record<string, boolean> = {};
    Object.keys(bulkFilters).forEach((bulkFilterKey) => {
      const bulkFilter = bulkFilters[bulkFilterKey];
      bulkQuery.push({
        filterKey: bulkFilterKey,
        filterValue: {
          includedKeys: bulkFilter.values,
        },
        filterType: FilterComponentType.MULTILEVEL_CHECKBOX, // This is static since only this type needs bulk search
        showExactMatch: bulkFilter.showExactMatch,
      });
      if (bulkFilter?.values?.length) {
        newBulkFilterSyncKeys[bulkFilterKey] = true;
      }
    });
    handleBulkQuery(bulkQuery);

    setTimeout(() => setBulkFilterSyncKey(newBulkFilterSyncKeys), 0);
  };

  const sideNavClassName = classNames([
    'leads-sidebar',
    'leadsSideNav',
    {
      'sidenav--collapse': !isSidebarExpanded,
    },
  ]);

  return (
    <div className={`leads-navigation ${sideNavClassName}`}>
      <LeadsFilterBulkSearch
        show={showBulkSearch}
        onClose={() => setShowBulkSearch(false)}
        onSearch={onBulkSearch}
        focusKey={bulkSearchFocusFilterKey}
      />
      <div
        className="expand-btn"
        {...accessibleOnClick(onSidebarExpansionChange)}
      >
        {isSidebarExpanded ? (
          <Icons.ChevronLeft width={17} height={17} className="gray-txt-15" />
        ) : (
          <Icons.ChevronRight width={17} height={17} className="gray-txt-15" />
        )}
      </div>
      <div className="leads-tabbing">
        {isSidebarExpanded && (
          <Tabs
            defaultActiveKey={
              isRestrictedV1Access
                ? LeadFinderTabKey.SAVED_TAB
                : LeadFinderTabKey.PEOPLE_TAB
            }
            activeKey={activeTabKey}
            onChange={(key) => handleTabChange(key)}
            style={{ zIndex: 1000 }}
            prefixCls="bs-tabs"
            className="bs-tabs-small"
          >
            {!isRestrictedV1Access && (
              <TabPane tab="People" key={LeadFinderTabKey.PEOPLE_TAB}>
                <PeopleTabComponent
                  {...{
                    handleRecentSearch,
                    handleGeneratePayload,
                    setSelectedLeads,
                    onBulkSearchShow,
                    handleDeleteFilter,
                    handleGetAllKeysByFilterFromQuery,
                    handleIndividualTag,
                    handleFoundValueExceptFromList,
                    handleQuery,
                    bulkFilterSyncKey,
                    showPhoneNumberNudge,
                    setShowPhoneNumberNudge,
                    setIgnorePhoneNumberNudge,
                    searchLeads,
                    isLoading,
                    keywordInputRef,
                    activeTabKey,
                  }}
                />
              </TabPane>
            )}

            {!leadFinderViewOnly() && (
              <TabPane tab="Saved" key={LeadFinderTabKey.SAVED_TAB}>
                <SavedTab
                  {...{
                    leadTags,
                    isLeadTagsLoading,
                    activeTabKey,
                    savedFilters,
                    setSavedFilters,
                  }}
                />
              </TabPane>
            )}
          </Tabs>
        )}
      </div>
    </div>
  );
};

export default LeadsSidebar;
